// Avoid re-exports in favor of direct imports or entry-point files
/* eslint-disable no-barrel-files/no-barrel-files */
import { graphql } from 'relay-runtime';
import { useState, useContext, useEffect } from 'react';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { fg } from '@confluence/feature-gating';

import type {
	useFeedTypeSelectorFeedTypeSelectorRefreshMutation as useFeedTypeSelectorFeedTypeSelectorRefreshMutation$data,
	useFeedTypeSelectorFeedTypeSelectorRefreshMutationVariables as useFeedTypeSelectorFeedTypeSelectorRefreshMutation$variables,
} from './__types__/useFeedTypeSelectorFeedTypeSelectorRefreshMutation.ts';
import type {
	useFeedTypeSelectorRefreshQuery$data,
	useFeedTypeSelectorRefreshQuery$variables,
} from './__generated__/useFeedTypeSelectorRefreshQuery.graphql.ts';
import { FeedType } from './feedTypeEnum.ts';

const useFeedTypeSelectorRefreshQuery = graphql`
	query useFeedTypeSelectorRefreshQuery {
		userPreferences {
			__typename
			feedTab
		}
	}
`;

const useFeedTypeSelectorFeedTypeSelectorRefreshMutation = graphql`
	mutation useFeedTypeSelectorFeedTypeSelectorRefreshMutation($feedTab: String!) {
		updateUserPreferences(userPreferences: { feedTab: $feedTab }) {
			feedTab
		}
	}
`;

type UseFeedTypeSelector = (defaultPreference?: FeedType) => {
	loading: boolean;
	queryError?: ApolloErrorInterop;
	mutationError?: ApolloErrorInterop;
	feedType: FeedType | string;
	updateFeedType: (feedType: FeedType | string) => void;
	hasSelectedFeedTypePreference: boolean;
};

type UseFeedTypeOverride = () => {
	feedTypeOverride?: FeedType;
	clearFeedTypeOverride: () => void;
};

export const useFeedTypeSelector: (defaultPreference?: FeedType) => {
	feedType: FeedType;
	hasSelectedFeedTypePreference: boolean | StringConstructor;
	mutationError: any;
	queryError: any;
	loading: any;
	updateFeedType: (updatedFeedType: FeedType | string) => void;
} = (defaultPreference = FeedType.FOLLOWING) => {
	const { isLoggedIn } = useSessionData();
	const writeFragmentInterop = useWriteFragmentInterop<any>({
		fragmentSpec: graphql`
			fragment useFeedTypeSelectorWriteFragment on UserPreferences {
				feedTab
			}
		`,
	});

	const {
		data,
		loading,
		error: queryError,
	} = useQueryInterop<
		useFeedTypeSelectorRefreshQuery$data,
		useFeedTypeSelectorRefreshQuery$variables
	>(useFeedTypeSelectorRefreshQuery, {
		skip: !isLoggedIn,
	});

	// @ts-ignore
	const feedType = (data?.userPreferences.feedTab as FeedType) || defaultPreference;
	const [feedTypeSelectorMutation, { error: mutationError }] = useMutationInterop<
		useFeedTypeSelectorFeedTypeSelectorRefreshMutation$data,
		useFeedTypeSelectorFeedTypeSelectorRefreshMutation$variables
	>(useFeedTypeSelectorFeedTypeSelectorRefreshMutation);
	const updateFeedType = (updatedFeedType: FeedType | string) => {
		if (isLoggedIn) {
			const variables = {
				feedTab: updatedFeedType as String,
			};

			void feedTypeSelectorMutation({
				// @ts-ignore
				variables,
				optimisticResponse: {
					// Relay does not allow `__typename` field on Query, Mutation or Subscription.
					// @ts-ignore __typename does not exist on autogenerated graphql types
					__typename: 'Mutation',
					updateUserPreferences: {
						__typename: 'UserPreferences',
						feedTab: updatedFeedType as FeedType,
					},
				},
			}).catch(() => {}); // Ignore the error here since it is handled in useMutation

			// Always write to the cache so the user sees the preference even if there is an error
			if (expValEquals('remove_usages_of_cache_read_write', 'isEnabled', true)) {
				writeFragmentInterop({
					fragmentId: '$ROOT_QUERY.userPreferences',
					data: {
						...variables, //handles either feedType or feedTab depending on FF
						__typename: 'UserPreferences',
					},
				});
			} else {
				writeQueryInterop({
					query: useFeedTypeSelectorRefreshQuery,
					data: {
						userPreferences: {
							__typename: 'UserPreferences',
							...variables, //handles either feedType or feedTab depending on FF
						},
					},
				});
			}
		}
	};

	return {
		loading,
		queryError,
		mutationError,
		feedType,
		updateFeedType,
		// @ts-ignore
		hasSelectedFeedTypePreference: Boolean(data?.userPreferences.feedTab),
	};
};

export const useAnonymousFeedTypeSelector: UseFeedTypeSelector = (
	defaultPreference = FeedType.FOLLOWING,
) => {
	const [feedType, setFeedType] = useState<FeedType | string>(defaultPreference);
	const [hasSelectedFeedTypePreference, setHasSelectedFeedTypePreference] =
		useState<boolean>(false);
	return {
		loading: false,
		feedType,
		updateFeedType: (feedType: FeedType | string) => {
			setFeedType(feedType);
			setHasSelectedFeedTypePreference(true);
		},
		hasSelectedFeedTypePreference,
	};
};

const getFeedType = (feedQueryParam?: FeedType) =>
	feedQueryParam && Object.values(FeedType).includes(feedQueryParam) ? feedQueryParam : undefined;

export const useFeedTypeOverride: UseFeedTypeOverride = () => {
	const { getQueryParams, setQueryParams } = useContext(RoutesContext);
	const feedQueryParam = getSingleParam(getQueryParams(), 'feedType') as FeedType | undefined;
	const [feedTypeOverride, setFeedTypeOverride] = useState<FeedType | undefined>(
		getFeedType(feedQueryParam),
	);

	useEffect(() => {
		setQueryParams({ feedType: undefined }, true);
	}, [setQueryParams]);

	useEffect(() => {
		if (fg('resurrected_user_welcome_back_exp_gate')) {
			const feed = getFeedType(feedQueryParam);
			if (feed !== undefined) {
				setFeedTypeOverride(feed);
				setQueryParams({ feedType: undefined }, true);
			}
		}
	}, [feedQueryParam, setQueryParams]);

	const clearFeedTypeOverride = () => {
		setFeedTypeOverride(undefined);
	};
	return {
		feedTypeOverride,
		clearFeedTypeOverride,
	};
};

export { FeedType } from './__types__/FeedTypeSelectorMutation.ts';
